import { ApiService } from "./api.service";
import type { ITeamsAppPackage, TTeamsConnection, TTeamsSignInStart } from "@/types/msteams";

/** The Microsoft Teams connection endpoints (plan §5a, NG-1096).
 *
 *  The resource is **singular** — a workspace consents to one Entra tenant, which the
 *  partial unique constraint on the table also says — so there is no collection to
 *  list and no id in any path. That is the one structural difference from the LMS
 *  connection service beside this one; the verb-per-decision shape
 *  (`enable`/`disable`/`disconnect` rather than a PATCH of `is_enabled`) is
 *  deliberately the same, because they are three different decisions with three
 *  different consequences.
 *
 *  **Read-only apart from those verbs** (NG-1187). The one PATCH here set the
 *  workspace's notification-category allowlist, and there is no allowlist any more:
 *  scoping is per person, on Profile → Notifications. The endpoint is gone, so a
 *  method for it would 405.
 *
 *  Response-time budgets (`/response-time-limits`): every method here is a
 *  single-row read or write that returns promptly except two, and both are in the
 *  1–10s band with a visible indicator owned by the card — `startSignIn`, which mints
 *  an Entra authorization URL through `msal`, and `sendTestCard`, which makes a live
 *  Bot Connector call.
 */
export class MsTeamsConnectionService extends ApiService {
  private base(slug: string): string {
    return `/api/workspaces/${slug}/integrations/msteams`;
  }

  /** The connection, or the `not_enabled` shape when the workspace has none.
   *
   *  Never a 404: "is Teams on here" is a question a workspace with no row has a real
   *  answer to, and a frontend that had to branch on the HTTP status to know which
   *  shape it got is how a "disabled" state ends up rendering as an error.
   */
  async getConnection(slug: string): Promise<TTeamsConnection> {
    return this.get(`${this.base(slug)}/connection/`);
  }

  /** Where to send the admin's browser to sign in to Microsoft.
   *
   *  A URL rather than a 302, because a redirect to Microsoft on an XHR is followed by
   *  the browser and lands a cross-origin HTML page in a JSON parser. `returnPath` is
   *  the web-app path the callback redirects back to with `msteams_connected=1` or
   *  `msteams_error=<code>` appended; the server refuses anything that is not a
   *  single-slash path and falls back to `/settings/integrations`.
   *
   *  Answers 503 (`code: "signin_not_configured"`) where the sign-in registration is
   *  unset — that is this flow's whole per-environment gate.
   */
  async startSignIn(slug: string, returnPath: string): Promise<TTeamsSignInStart> {
    return this.post(`${this.base(slug)}/connection/sign-in/`, { return_path: returnPath });
  }

  /** Un-pause a workspace that disabled the channel. 404 when there is no row —
   *  a workspace with no connection has no tenant id, and the sign-in is the way in. */
  async enable(slug: string): Promise<TTeamsConnection> {
    return this.post(`${this.base(slug)}/connection/enable/`);
  }

  /** Stop every delivery in the workspace at once, keeping the links so re-enabling
   *  is one click and nobody has to re-install. */
  async disable(slug: string): Promise<TTeamsConnection> {
    return this.post(`${this.base(slug)}/connection/disable/`);
  }

  /** End the consent: the row is soft-deleted, its links become `uninstalled` and
   *  their pending deliveries are dropped. Idempotent — a workspace with no
   *  connection gets the same 200 and the same `not_enabled` body. */
  async disconnect(slug: string): Promise<TTeamsConnection> {
    return this.post(`${this.base(slug)}/connection/disconnect/`);
  }

  /** Send a card to **the caller's own** Teams chat and record the outcome.
   *
   *  A vendor failure is still a 200 with `last_test_status: false` and the reason in
   *  `last_test_message` — a failed test is a fact to show beside the connection, not
   *  a request that went wrong. A caller with no matched link is different and answers
   *  409 (`code: "no_linked_chat"`): nothing was attempted, and the fix is to install
   *  the app rather than to try again.
   */
  async sendTestCard(slug: string): Promise<TTeamsConnection> {
    return this.post(`${this.base(slug)}/connection/test-card/`);
  }

  /** The zip a customer's Teams admin uploads to their org catalog.
   *
   *  Goes through the shared axios instance directly rather than `this.get`, because
   *  the filename is in `Content-Disposition` and `ApiService.get` returns only
   *  `res.data`. The server names the package per environment and per version
   *  (`cognota-teams-<label>-<version>.zip`): the label says which bot id the manifest
   *  carries, and the version is the only way an admin can tell a package they saved
   *  months ago from the one they just downloaded, because nothing on our side can
   *  read what a tenant has installed. A client-side constant would lose both.
   *
   *  Not in the OpenAPI schema by deliberate exclusion (it answers
   *  `application/zip`), which is also why it is typed here rather than derived.
   *  503 (`code: "bot_not_configured"`) where the bot app id is unset.
   */
  async downloadAppPackage(slug: string): Promise<ITeamsAppPackage> {
    const response = await this.axiosInstance.get<Blob>(`${this.base(slug)}/app-package/`, {
      responseType: "blob",
    });
    return {
      blob: response.data,
      filename: filenameFromDisposition(response.headers?.["content-disposition"]),
    };
  }
}

/** The name the fallback below lands on. Module-local: nothing outside this file has
 *  a reason to know it, and exporting it for a test's benefit is how knip ends up
 *  policing a constant instead of an API. */
const DEFAULT_APP_PACKAGE_FILENAME = "cognota-teams.zip";

/** `attachment; filename="cognota-teams-stg-1.0.1.zip"` → `cognota-teams-stg-1.0.1.zip`.
 *
 *  Falls back to an unlabelled name rather than throwing: a missing or unreadable
 *  header is a cosmetic problem with the file's name, and refusing the download over
 *  it would deny the admin the one artefact their Teams admin needs.
 */
function filenameFromDisposition(disposition: unknown): string {
  if (typeof disposition !== "string") return DEFAULT_APP_PACKAGE_FILENAME;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  return match?.[1]?.trim() || DEFAULT_APP_PACKAGE_FILENAME;
}
